<script setup>

import {computed, nextTick, onMounted, ref} from "vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'

const notificationStore = useNotificationStore()

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    }
})

const loaded = ref(false)
const courseLicenses = ref([])
const revokedCourseLicenses = ref([])
const restoringLicenseId = ref(null)
const canAssign = ref(false)
const assignableCourses = ref([])
const selectedCourseId = ref('')
const assigning = ref(false)
const licenseAssigningVisible = ref(false)

const loadCourseLicenses = async () => {
    const res = await axios.get(route('crm.leads.licenses', {id: props.leadId, type: 'course'}))

    courseLicenses.value = res.data.licenses
    revokedCourseLicenses.value = res.data.revoked_licenses ?? []
    canAssign.value = Boolean(res.data.can_assign)
    assignableCourses.value = res.data.assignable_courses ?? []
    loaded.value = true
}

const revokeLicense = async (license) => {
    const res = await axios.delete(route('crm.leads.license.delete', {id: props.leadId, type: 'course', license_id: license.id}))

    if (res.data.success) {
        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:licenses.revoked'),
            message: license.course.name,
            dismissAfter: 5000,
        })

        await loadCourseLicenses()
    }
}

const restoreLicense = async (license) => {
    if (restoringLicenseId.value !== null) {
        return
    }
    restoringLicenseId.value = license.id

    try {
        const res = await axios.post(route('crm.leads.license.restore', {id: props.leadId, type: 'course', license_id: license.id}))

        if (res.data.success) {
            notificationStore.addNotification({
                type: 'success',
                title: _mft('crm:licenses.restored'),
                message: license.course.name,
                dismissAfter: 5000,
            })

            await loadCourseLicenses()
        }
    } finally {
        restoringLicenseId.value = null
    }
}

const assignLicense = async () => {
    if (assigning.value || !selectedCourseId.value) {
        return
    }
    assigning.value = true

    try {
        const course = assignableCourses.value.find(c => String(c.id) === String(selectedCourseId.value))
        const res = await axios.post(route('crm.leads.license.store', {id: props.leadId, type: 'course'}), {
            course_id: Number(selectedCourseId.value),
        })

        if (res.data.success) {
            notificationStore.addNotification({
                type: 'success',
                title: _mft('crm:licenses.assigned'),
                message: course?.name ?? '',
                dismissAfter: 5000,
            })

            selectedCourseId.value = ''
            await loadCourseLicenses()
        }
    } finally {
        assigning.value = false
        licenseAssigningVisible.value = false
    }
}

const initAssignLicense = () => {
    licenseAssigningVisible.value = true

    nextTick(() => {
        document.getElementById('assign-new-license').scrollIntoView({behavior: 'smooth'})
    })
}

const totalLicenseCount = computed(() => {
    return courseLicenses.value.length
})

onMounted(() => {
    loadCourseLicenses()
})

</script>
<template>
    <div class="mb-8">
        <div
            v-if="!loaded"
            class="flex justify-center"
        >
            <mf-spinner-medium class="w-12 h-6" />
        </div>

        <list-basic>
            <template #header>
                <div class="flex justify-between">
                    <h2>{{ _mft('crm:licenses.title') }}</h2>
                    <button-secondary
                        v-if="canAssign && loaded"
                        @click="initAssignLicense()"
                    >
                        {{ _mft('crm:licenses.assign') }}
                    </button-secondary>
                </div>
            </template>

            <list-basic-item v-if="licenseAssigningVisible && canAssign && loaded">
                <h3>{{ _mft('crm:licenses.assignTitle') }}</h3>
                <div
                    v-if="assignableCourses.length > 0"
                    class="flex items-end gap-2"
                >
                    <input-select
                        v-model:content="selectedCourseId"
                        :options="assignableCourses"
                        value-specification="id"
                        label-specification="name"
                        :label="_mft('crm:licenses.assignCourse')"
                        name="course_id"
                        class="flex-1 !mb-0"
                    />
                    <button-primary
                        size="sm"
                        :disabled="!selectedCourseId || assigning"
                        :state="assigning ? 'waiting' : ''"
                        @click="assignLicense"
                    >
                    <span class="whitespace-nowrap">
                        {{ _mft('crm:licenses.assign') }}
                    </span>
                    </button-primary>
                </div>
                <span
                    v-else
                    class="text-sm text-gray-500"
                >
                    {{ _mft('crm:licenses.noAssignableCourses') }}
                </span>
            </list-basic-item>


            <list-basic-item
                v-for="(license, i) in courseLicenses"
            >
                <template #title>
                    {{ license.course.name }}
                </template>
                <div class="text-gray-400 text-xs">
                    <span>
                        {{ _mft('crm:licenses.acquired') }} {{ license.created_at.user_tz_display }}
                    </span>
                    <span
                        v-if="license.created_at.relative > 1"
                        class="text-gray-400 ml-1"
                    >
                        ({{ Math.floor(license.created_at.relative) }} {{ _mft('crm:licenses.acquiredMonthsAgo') }})
                    </span>
                </div>
                <template #actions>
                    <button-with-confirmation
                        style-type="danger"
                        size="sm"
                        @confirmed="revokeLicense(license)"
                        :confirm-text="_mft('shared:confirm.areYouSure')"
                        confirm-type="danger"
                        :confirm-button-label="_mft('crm:licenses.revoke')"
                        :abort-button-label="_mft('shared:modal.defaultCancel')"
                    >
                        <span class="whitespace-nowrap">
                            {{ _mft('crm:licenses.revoke') }}
                        </span>
                    </button-with-confirmation>
                </template>
            </list-basic-item>
            <list-basic-item
                v-if="totalLicenseCount === 0 && loaded"
            >
                {{ _mft('crm:licenses.noLicensesFound') }}
            </list-basic-item>
        </list-basic>

        <list-basic
            v-if="revokedCourseLicenses.length > 0"
            class="mt-4"
        >
            <template #header>
                <h3>{{ _mft('crm:licenses.revokedTitle') }}</h3>
            </template>
            <list-basic-item
                v-for="license in revokedCourseLicenses"
                :key="license.id"
            >
                <template #title>
                    <span class="text-gray-500">{{ license.course.name }}</span>
                </template>
                <div class="text-gray-400 text-xs">
                    {{ _mft('crm:licenses.revokedOn') }} {{ license.revoked_at.user_tz_display }}
                </div>
                <template #actions>
                    <button-secondary
                        size="sm"
                        :disabled="restoringLicenseId !== null"
                        :state="restoringLicenseId === license.id ? 'waiting' : ''"
                        @click="restoreLicense(license)"
                    >
                        <span class="whitespace-nowrap">
                            {{ _mft('crm:licenses.restore') }}
                        </span>
                    </button-secondary>
                </template>
            </list-basic-item>
        </list-basic>

    </div>
</template>
